import {useMutation} from "@apollo/client";
import {toast} from "react-hot-toast";
import {Link} from "react-router";

import {useCurrentUserContext} from "@/components/context/hooks.ts";
import {shortDateTimeUser} from "@/lib/format.tsx";
import {GQL_PURCHASE_REQUEST, PurchaseRequestResponse} from "@/lib/queries/billing.ts";
import {PlanLimit} from "@/lib/types/accounts.ts";
import {BillingPeriod, Plan} from "@/lib/types/billing.ts";
import {CheckTypeDisplay, MonitoringServiceType} from "@/lib/types/checks.ts";
import {InstantPopUp} from "@/pages/billing/v1/instantCheckout/InstantPopUp.tsx";
import {LegacyPlanBox} from "@/pages/billing/v1/instantCheckout/LegacyPlanBox.tsx";

interface PopupRequestUpgradePlanProps {
  show: boolean;
  onComplete: () => void;
  service: MonitoringServiceType;
  plan: string;
  price: number;
  limitField: string;
  period: BillingPeriod;
  planLimit: PlanLimit;
  requestedAt?: string;
}

export const PopupRequestUpgradePlan = ({
  show,
  service,
  onComplete,
  plan,
  price,
  period,
  limitField,
  planLimit,
  requestedAt,
}: PopupRequestUpgradePlanProps) => {
  const {user} = useCurrentUserContext();
  const [purchaseRequest, {loading}] = useMutation<PurchaseRequestResponse>(GQL_PURCHASE_REQUEST);
  const amount = planLimit.bundleSize;
  const usedAny = planLimit.used > 0;
  const callout = usedAny
    ? `You have reached the limit of ${planLimit.allowed} ${CheckTypeDisplay[service]} Checks on your current plan`
    : `${CheckTypeDisplay[service]} Checks are not available on your current plan`;
  return (
    <InstantPopUp
      show={show}
      loading={loading}
      onConfirm={async () => {
        const r = await purchaseRequest({
          variables: {
            addedValues: [
              {
                limit_field: limitField,
                amount: amount,
              },
            ],
          },
        });
        if (r.data?.purchaseRequest.success) {
          toast.success("Request sent successfully. The account owner will review your request.");
        }
        onComplete();
      }}
      title={`Upgrade to add ${CheckTypeDisplay[service]} Checks`}
      alertCallout={callout}
      alertChildren={
        <>
          <span>
            If you would like to request upgrading your subscription to add {CheckTypeDisplay[service]} Checks, please
            send a request to the owner of this account below to upgrade. If you have any questions don't hesitate
            to&nbsp;
            <Link to="/contact">contact us</Link>
          </span>
          {requestedAt && (
            <div>
              <br />
              Your request has been sent at: {shortDateTimeUser(requestedAt, user!)}
            </div>
          )}
        </>
      }
      onCancel={onComplete}
      btnTitle="Request Upgrade"
    >
      <LegacyPlanBox plan={plan as Plan} price={price} period={period} />
    </InstantPopUp>
  );
};
